/** @jsxImportSource @emotion/react */
"use client";

import { useFormatter, useTranslations } from "@i18n/react";
import type {
  CommunitiesStatsTimeGraphType,
  GetDateByRangeProps,
} from "@packages/sdk";
import {
  getDateByRange,
  useRequestCommunitiesStatsTotals,
} from "@packages/sdk";
import { useMemo } from "react";

import { useErrorNotification } from "../../../../../hooks/useErrorNotification.emotion";
import { StatisticCard } from "../../../../_base/dashboard/StatisticCard/StatisticCard.emotion";
import type { DashboardMetricConfig } from "../dashboardMetrics.types";

export type CommunityTotalsStatisticCardsProps = {
  id: number;
  /**
   * The metric tiles to render, in display order. Already filtered for the
   * community — the caller owns visibility so the tiles cannot disagree with
   * whatever else it drives from the same list (e.g. the chart).
   */
  metrics: DashboardMetricConfig[];
  range?: GetDateByRangeProps["range"] | "all";
  selectedType: CommunitiesStatsTimeGraphType;
  onSelectMetric: (metric: DashboardMetricConfig) => void;
};

export const useCommunityTotalsStatisticCards = ({
  id,
  metrics,
  range = "all",
  selectedType,
  onSelectMetric,
}: CommunityTotalsStatisticCardsProps) => {
  const format = useFormatter();
  const t = useTranslations();

  const { query: queryTotals } = useRequestCommunitiesStatsTotals({
    id,
    startDate: range === "all" ? undefined : getDateByRange({ range }),
  });

  useErrorNotification({ isError: queryTotals.isError });

  const statisticCards = useMemo(() => {
    const formatValue = (metric: DashboardMetricConfig) => {
      const value = queryTotals.data?.[metric.totalsField] ?? 0;
      return metric.isPercent
        ? format.number(value / 100, {
            style: "percent",
            maximumFractionDigits: 2,
          })
        : format.number(value);
    };

    return metrics.map((metric) => (
      <StatisticCard
        key={metric.type}
        value={formatValue(metric)}
        label={t(metric.labelKey)}
        loading={!queryTotals.data}
        selected={metric.type === selectedType}
        onClick={() => onSelectMetric(metric)}
      />
    ));
  }, [metrics, queryTotals.data, selectedType, onSelectMetric, format, t]);

  return { statisticCards };
};
